import { useState, useRef, useMemo, useCallback } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from './ui/badge';
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover';
import { Command, CommandList, CommandItem, CommandEmpty } from './ui/command';
import { cn } from '@/lib/utils';

interface TagInputProps {
  value: string;
  onChange: (value: string) => void;
  existingTags: { name: string }[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function TagInput({
  value,
  onChange,
  existingTags,
  placeholder = '태그 입력',
  disabled = false,
  className,
}: TagInputProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse current tags from comma-separated value
  const currentTags = useMemo(
    () => value.split(',').map((t) => t.trim()).filter(Boolean),
    [value]
  );

  // Filter suggestions (exclude already selected)
  const filteredTags = useMemo(() => {
    const currentLower = new Set(currentTags.map((t) => t.toLowerCase()));
    const available = existingTags.filter((t) => !currentLower.has(t.name.toLowerCase()));

    if (!inputValue) return available;
    return available.filter((t) =>
      t.name.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [existingTags, currentTags, inputValue]);

  // Check if input value is a new tag (not in existing tags)
  const isNewTag = useMemo(() => {
    if (!inputValue.trim()) return false;
    const inputLower = inputValue.trim().toLowerCase();
    return !existingTags.some((t) => t.name.toLowerCase() === inputLower);
  }, [inputValue, existingTags]);

  const addTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();
      if (!trimmedTag) return;

      // Check if already exists (case-insensitive)
      const existsAlready = currentTags.some(
        (t) => t.toLowerCase() === trimmedTag.toLowerCase()
      );
      if (existsAlready) {
        setInputValue('');
        return;
      }

      const newTags = [...currentTags, trimmedTag];
      onChange(newTags.join(', '));
      setInputValue('');
      inputRef.current?.focus();
    },
    [currentTags, onChange]
  );

  const removeTag = useCallback(
    (tag: string) => {
      const newTags = currentTags.filter((t) => t !== tag);
      onChange(newTags.join(', '));
    },
    [currentTags, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Comma or Enter to add current input as new tag
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    }

    // Backspace to remove last tag when input is empty
    if (e.key === 'Backspace' && !inputValue && currentTags.length > 0) {
      removeTag(currentTags[currentTags.length - 1]);
    }

    // Escape to close popover
    if (e.key === 'Escape') {
      setOpen(false);
    }

    // Arrow down to focus on first suggestion
    if (e.key === 'ArrowDown' && open && filteredTags.length > 0) {
      e.preventDefault();
      const firstItem = containerRef.current?.querySelector('[cmdk-item]');
      if (firstItem instanceof HTMLElement) {
        firstItem.focus();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // If user types comma, add the part before comma as tag
    if (newValue.includes(',')) {
      const parts = newValue.split(',');
      const tagToAdd = parts[0].trim();
      if (tagToAdd) {
        addTag(tagToAdd);
      }
      setInputValue(parts.slice(1).join(','));
    } else {
      setInputValue(newValue);
    }
  };

  const showSuggestions = open && (filteredTags.length > 0 || (!!inputValue.trim() && isNewTag));

  return (
    <Popover open={showSuggestions} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={containerRef}
          className={cn(
            'flex flex-wrap gap-1.5 p-2 border rounded-md min-h-[38px] items-center bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {currentTags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="text-xs h-6 gap-1 pr-1"
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            onBlur={(e) => {
              // Delay closing to allow clicking on suggestions
              setTimeout(() => {
                if (!containerRef.current?.contains(document.activeElement)) {
                  setOpen(false);
                  // Add remaining input as tag on blur
                  if (inputValue.trim()) {
                    addTag(inputValue);
                  }
                }
              }, 150);
            }}
            onKeyDown={handleKeyDown}
            placeholder={currentTags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[80px] outline-none bg-transparent text-sm"
            disabled={disabled}
          />
        </div>
      </PopoverAnchor>

      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandList>
            {filteredTags.length === 0 && !inputValue.trim() && (
              <CommandEmpty>태그를 입력하세요</CommandEmpty>
            )}
            {filteredTags.map((tag) => (
              <CommandItem
                key={tag.name}
                value={tag.name}
                onSelect={() => addTag(tag.name)}
              >
                {tag.name}
              </CommandItem>
            ))}
            {inputValue.trim() && isNewTag && (
              <CommandItem
                value={`new-${inputValue}`}
                onSelect={() => addTag(inputValue.trim())}
                className="text-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                "{inputValue.trim()}" 새 태그 추가
              </CommandItem>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
